/**
 * Recuperacion cuando la aplicacion falla.
 *
 * ORIGEN: la aplicacion se rompio en el telefono y quedo atorada.
 *
 * Al estar configurada para PREGUNTAR antes de actualizar (Req. 2.5), el boton
 * de actualizar vive dentro de la propia aplicacion. Si el fallo ocurre antes
 * de poder mostrarlo, no queda salida y hay que borrar el icono, lo que en iOS
 * BORRA TAMBIEN LOS DATOS. Este modulo evita llegar a ese punto.
 *
 * DISTINCION IMPORTANTE:
 *   Los cachés del Service Worker guardan los ARCHIVOS del programa.
 *   Los datos del negocio viven en IndexedDB.
 *   Borrar los cachés NO borra recetas, compras ni ventas.
 */

export interface ResultadoRecuperacion {
  exito: boolean;
  mensaje: string;
}

export function hayConexion(): boolean {
  // navigator.onLine es una pista, no una garantia: puede decir true sin
  // internet real. Basta para decidir entre las dos estrategias.
  return typeof navigator === 'undefined' ? true : navigator.onLine !== false;
}

/**
 * Fuerza la version nueva: actualiza el Service Worker, descarta los archivos
 * guardados y recarga.
 *
 * SOLO debe usarse con conexion. Sin señal, borrar los archivos dejaria la
 * aplicacion sin poder abrirse, que es peor que el fallo original.
 */
export async function forzarActualizacion(): Promise<ResultadoRecuperacion> {
  if (!hayConexion()) {
    return {
      exito: false,
      mensaje:
        'No hay conexion. Conectate a internet antes de actualizar: sin señal, ' +
        'descartar los archivos guardados dejaria la aplicacion sin poder abrirse.',
    };
  }

  try {
    if ('serviceWorker' in navigator) {
      const registros = await navigator.serviceWorker.getRegistrations();
      for (const registro of registros) {
        try {
          await registro.update();
        } catch {
          // Si no se puede consultar al servidor, se sigue con lo demas.
        }
        // Pide al trabajador en espera que tome el control de inmediato.
        registro.waiting?.postMessage({ type: 'SKIP_WAITING' });
      }
    }

    // Se descartan los archivos del programa para obligar una descarga limpia.
    // Los datos del negocio no viven aqui: estan en IndexedDB.
    if ('caches' in globalThis) {
      const nombres = await caches.keys();
      await Promise.all(nombres.map((nombre) => caches.delete(nombre)));
    }

    recargar();
    return { exito: true, mensaje: 'Actualizando…' };
  } catch (error) {
    return {
      exito: false,
      mensaje: `No se pudo actualizar: ${error instanceof Error ? error.message : 'error desconocido'}`,
    };
  }
}

/**
 * Recarga sin tocar los archivos guardados.
 * Sirve para fallos pasajeros y funciona sin conexion.
 */
export function recargar(): void {
  // Se evita la caché del navegador agregando un parametro, sin descartar
  // los archivos del Service Worker.
  window.location.reload();
}

/** Vuelve al inicio de la aplicacion, util cuando falla una pantalla concreta. */
export function irAlInicio(): void {
  window.location.hash = '#/';
  window.location.reload();
}

export interface ResultadoBusqueda {
  hayNueva: boolean;
  mensaje: string;
}

/**
 * Busca una version nueva a peticion de la usuaria.
 *
 * MOTIVO DE EXISTIR: iOS mantiene las aplicaciones instaladas suspendidas en
 * memoria. Al reabrirlas desde el icono no hace una carga real de la pagina, y
 * el Service Worker puede tardar en revisar si hay algo nuevo. Sin este boton,
 * actualizar depende de que iOS lo decida.
 */
export async function buscarActualizacion(): Promise<ResultadoBusqueda> {
  if (!('serviceWorker' in navigator)) {
    return {
      hayNueva: false,
      mensaje:
        'Este navegador no maneja versiones instaladas. Recarga la pagina para ver la mas reciente.',
    };
  }

  if (!hayConexion()) {
    return {
      hayNueva: false,
      mensaje: 'No hay conexion. Conectate a internet para poder buscar una version nueva.',
    };
  }

  try {
    const registros = await navigator.serviceWorker.getRegistrations();
    if (registros.length === 0) {
      return {
        hayNueva: false,
        mensaje:
          'La aplicacion no esta instalada como app. Agregala a tu pantalla de inicio desde Safari.',
      };
    }

    // Pregunta al servidor por una version nueva.
    for (const registro of registros) {
      await registro.update();
    }

    // Tras consultar, se revisa si quedo alguna esperando para tomar el control.
    const actualizados = await navigator.serviceWorker.getRegistrations();
    const enEspera = actualizados.some(
      (r) => r.waiting !== null || r.installing !== null,
    );

    return enEspera
      ? { hayNueva: true, mensaje: 'Hay una version nueva lista para instalarse.' }
      : { hayNueva: false, mensaje: 'Ya tienes la version mas reciente.' };
  } catch (error) {
    return {
      hayNueva: false,
      mensaje: `No se pudo consultar: ${error instanceof Error ? error.message : 'error desconocido'}`,
    };
  }
}

/** Estado del Service Worker, para mostrarlo en la pantalla de error. */
export async function estadoServiceWorker(): Promise<string> {
  if (!('serviceWorker' in navigator)) return 'no disponible';
  try {
    const registros = await navigator.serviceWorker.getRegistrations();
    if (registros.length === 0) return 'sin registrar';
    const registro = registros[0];
    if (registro === undefined) return 'sin registrar';
    if (registro.waiting !== null) return 'hay una version nueva esperando';
    if (registro.installing !== null) return 'instalando una version nueva';
    return 'activo y al dia';
  } catch {
    return 'no se pudo consultar';
  }
}
